import { LoadingOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import React from 'react';
import { Button, Col, Form, FormInstance, Image, Input, PageTitle, Row, Table } from 'components';
import { FilterIcon, PlusIcon, ViewIcon } from 'components/common/icons';
import { CountryTable } from 'models/Country';
import { AddBrandModal } from 'pages/admin/brands/addBrand';
import { useBrands } from 'pages/admin/brands/useBrands';
import styles from 'pages/admin/clients/styles.module.scss';
import { Filters } from './filters';
import { HasAccess } from 'components/common/accessControl';
import { PERMISSIONS } from 'constants/permissions';
import { Tooltip } from 'antd';

const { Search } = Input;

type IndustryType = {
  name: string;
  id: string;
};

export interface BrandDataType {
  id: string;
  uuid: number | string;
  name: string;
  country: CountryTable;
  industry: IndustryType;
  formattedCreatedDate: string;
  productsCount: number;
  purchaseRequests: number;
}

export const Brands: React.FC = () => {
  const [filtersForm] = Form.useForm<FormInstance>();
  const {
    addModalState,
    onAddModalStateChange,
    onCloseFilters,
    onOpenFilters,
    openFilters,
    handleTableChange,
    routeChange,
    isLoading,
    pagination,
    onSearch,
    data,
    total,
    applyFilters,
    resetFilters,
  } = useBrands();

  const columns: ColumnsType<BrandDataType> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      render: (_, { uuid }) => {
        return uuid;
      },
    },
    {
      title: 'Brand Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      width: 200, // Set max width
      ellipsis: true, // Enable ellipsis
      render: (value) => {
        return (
          <Tooltip title={value}>
            <div className={'text-ellipsis'}>{value}</div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      render: (country) => {
        return (
          <Row wrap={false} align={'middle'} className={'gap-12'}>
            {country?.fileLocation && (
              <Image
                src={country?.fileLocation}
                width={18}
                height={18}
                preview={false}
                style={{ verticalAlign: 'unset', width: '18px' }}
                className={'radius-circle'}
              />
            )}
            <Col>{country?.name}</Col>
          </Row>
        );
      },
    },
    {
      title: 'Industry',
      dataIndex: 'industry',
      key: 'industry',
      render: (value) => value?.name,
    },
    {
      title: 'Created Date',
      dataIndex: 'formattedCreatedDate',
      key: 'createDate',
      render: (value) => value?.day,
      minWidth: 130,
      sorter: true,
    },
    {
      title: 'Last Updated Date',
      dataIndex: 'formattedLastModifiedDate',
      key: 'lastModifiedDate',
      render: (value) => value?.day,
      minWidth: 160,
      sorter: true,
    },
    {
      title: 'Pricelist Validity',
      dataIndex: 'formattedPricelistValidityDate',
      key: 'priceListValidityDate',
      render: (value) => value?.day,
      minWidth: 150,
      sorter: true,
    },
    {
      title: 'Sales Manager',
      dataIndex: 'salesManagers',
      key: 'salesManagers',
      render: (value) => {
        if (value?.length) {
          return <Tooltip title={value.join(', ')}>{value[0]}</Tooltip>;
        }
        return '';
      },
      minWidth: 120,
      sorter: false,
    },
    {
      title: 'Purchase Manager',
      dataIndex: 'purchaseManager',
      key: 'purchaseManager',
      render: (value) => `${value?.firstName || ''} ${value?.lastName || ''}`,
      minWidth: 150,
      sorter: false,
    },
    {
      title: 'Products Count',
      dataIndex: 'productsCount',
      key: 'productsCount',
      minWidth: 140,
      sorter: true,
    },
    {
      title: 'Purchase Requests',
      dataIndex: 'purchaseRequests',
      key: 'purchaseRequests',
      minWidth: 170,
      sorter: true,
    },
    {
      fixed: 'right',
      width: '36px',
      key: 'actions',
      render: (value) => (
        <Button onClick={() => routeChange(value.id)} type={'text'} size={'small'} icon={<ViewIcon />} />
      ),
    },
  ];

  return (
    <Col span={24} className={styles.main}>
      <Row gutter={[0, 24]}>
        <Col span={24}>
          <Row align={'middle'} justify={'space-between'}>
            <PageTitle title={'Brands'} />
            <HasAccess role={PERMISSIONS.brands.brand.write}>
              <Button type={'primary'} onClick={onAddModalStateChange} icon={<PlusIcon color={'#FFFFFF'} />}>
                Add New
              </Button>
            </HasAccess>
          </Row>
        </Col>
        <Col span={24} className={styles.main_container}>
          <Row>
            <Col span={24}>
              <Row justify={'end'} className={styles.actions_container}>
                <Col style={{ width: '300px' }}>
                  <Search enterButton={false} placeholder={'Search'} onChange={(e) => onSearch(e.target.value)} />
                </Col>
                <Button color="default" variant="filled" onClick={onOpenFilters} icon={<FilterIcon />} />
              </Row>
            </Col>
            <Col span={24} style={{ paddingTop: '20px' }}>
              <HasAccess role={PERMISSIONS.brands.brand.read}>
                <Table
                  tableLayout={'auto'}
                  bordered
                  scroll={{
                    x: true,
                  }}
                  rowKey="id"
                  size={'small'}
                  dataSource={data}
                  columns={columns}
                  loading={{
                    indicator: <LoadingOutlined style={{ fontSize: 48 }} spin />,
                    spinning: isLoading,
                  }}
                  locale={
                    isLoading
                      ? { emptyText: null } // Hide "No Data" text while loading
                      : {} // Use default AntD "No Data" icon when empty
                  }
                  onChange={handleTableChange}
                  pagination={{
                    position: ['bottomCenter'],
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} items`,
                    size: 'default',
                    current: pagination.page,
                    pageSize: pagination.size,
                    total: total,
                  }}
                />
              </HasAccess>
            </Col>
          </Row>
        </Col>
      </Row>
      <Filters
        form={filtersForm}
        drawerOpen={openFilters}
        closeDrawer={onCloseFilters}
        applyFilters={applyFilters}
        resetFilters={resetFilters}
      />
      <AddBrandModal modalState={addModalState} onModalStateChange={onAddModalStateChange} />
    </Col>
  );
};
